/**
 * Page-side PWA update-reload policy (TASK 11.5).
 *
 * `sw/service-worker.ts` posts {@link SW_MESSAGE_UPDATED} after a new worker
 * activates, and states plainly that the message is a COURTESY, not a command:
 * "an un-reloaded tab keeps working … and the next launch boots the new shell
 * anyway". So the page must reload ONLY when it can do so without disrupting
 * the user:
 *
 *   - NEVER on first install — a reload is pointless (and spurious) when no
 *     worker controlled the page at load: there is no prior shell to leave, and
 *     the freshly-installed one already controls the very next navigation.
 *   - NEVER mid-playback, and NEVER while the tab is visible — that is exactly
 *     the interruption the courtesy design exists to avoid.
 *
 * A genuine update therefore only ARMS a pending flag; the reload is applied
 * the first moment it is non-disruptive — the document is hidden AND audio is
 * not playing — which is typically the next visibilitychange-to-hidden. The
 * reload-loop guard ensures a single activation can never reload more than
 * once.
 *
 * Extracted from `main.ts` (whose module-eval side effects make it untestable)
 * so the policy can be driven directly under vitest.
 *
 * @module
 */

export interface UpdateReloadDeps {
  /**
   * True iff a service worker already controlled this page at initial load
   * (`navigator.serviceWorker.controller !== null`, captured before anything
   * else). False means this is a first install and no reload should ever fire.
   */
  readonly hadControllerAtLoad: boolean;
  /** Whether audio is actively playing right now (store-backed). */
  readonly isPlaying: () => boolean;
  /** Whether the document is currently visible. */
  readonly isVisible: () => boolean;
  /** Reloads the page. */
  readonly reload: () => void;
  /**
   * Subscribes to visibility changes; the supplied handler re-evaluates
   * whether a pending reload can now be applied.
   */
  readonly onVisibilityChange: (handler: () => void) => void;
}

export interface UpdateReloadController {
  /** Call for every {@link SW_MESSAGE_UPDATED} the page receives. */
  readonly onUpdateMessage: () => void;
}

/**
 * Builds the update-reload controller. Registers its own visibilitychange
 * subscription (via {@link UpdateReloadDeps.onVisibilityChange}) so a pending
 * reload is retried whenever the tab's visibility flips.
 */
export function createUpdateReloadController(deps: UpdateReloadDeps): UpdateReloadController {
  const { hadControllerAtLoad, isPlaying, isVisible, reload, onVisibilityChange } = deps;

  let updatePending = false;
  let reloading = false;

  function maybeReload(): void {
    if (!updatePending || reloading) return;
    // NEVER interrupt active use: a visible tab or live playback both defer.
    if (isVisible() || isPlaying()) return;
    reloading = true; // loop guard: a single activation reloads at most once
    reload();
  }

  onVisibilityChange(maybeReload);

  return {
    onUpdateMessage() {
      // First install (no controller at load): nothing worth reloading into.
      if (!hadControllerAtLoad) return;
      updatePending = true;
      maybeReload();
    },
  };
}
